
import { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import ReactDOMServer from 'react-dom/server';
import { MeshLocation } from '@/components/security/meshLocationsData';
import { toast } from '@/components/ui/use-toast';
import { useTheme } from '@/components/theme/ThemeProvider';

export const useMapMarkers = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  locations: MeshLocation[],
  mapLoaded: boolean
) => {
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupsRef = useRef<{ [key: number]: HTMLElement }>({});
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const cleanupMarkers = () => {
    if (markersRef.current.length > 0) {
      console.log('Cleaning up markers');
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    }
  };

  useEffect(() => {
    if (!mapLoaded || !map.current) {
      console.log('Map not loaded or not available yet');
      return;
    }

    try {
      console.log('Adding markers to map for', locations.length, 'locations');
      
      cleanupMarkers();

      locations.forEach(location => {
        // Create popup element
        const popupEl = document.createElement('div');
        popupsRef.current[location.id] = popupEl;
        
        // Create marker element
        const markerEl = document.createElement('div');
        markerEl.className = `mesh-location-marker`;
        
        // Set marker color based on status - brighter colors
        const pinColor = location.status === 'active' ? '#00FF66' : '#FF3A3A';
        
        markerEl.innerHTML = `
          <div style="color: ${pinColor}; transform: scale(1.2);">
            <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
              <circle cx="12" cy="10" r="4" fill="${isDark ? '#222222' : 'white'}" stroke="currentColor"></circle>
            </svg>
            <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
              <span style="font-size: 0.75rem; font-weight: bold; color: ${isDark ? '#f0f0f0' : '#1A1F2C'}; transform: translateY(-5px);">${location.id}</span>
            </div>
          </div>
        `;

        // Create popup
        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 25,
          maxWidth: '300px'
        }).setDOMContent(popupEl);
        
        // Create marker and add to map
        console.log(`Adding marker for ${location.name} at [${location.lng}, ${location.lat}]`);
        const marker = new mapboxgl.Marker(markerEl)
          .setLngLat([location.lng, location.lat])
          .setPopup(popup)
          .addTo(map.current!);

        // Render popup content when hovering over marker
        const renderPopup = () => {
          const htmlString = `
            <div class="p-3 min-w-[220px] ${isDark ? 'bg-card text-foreground' : 'bg-white'} rounded-lg border border-gray-200 shadow-lg">
              <div class="font-medium text-sm mb-1">${location.name}</div>
              <div class="flex items-center mb-2">
                <span class="inline-block w-2 h-2 rounded-full mr-1.5" style="background-color: ${pinColor};"></span>
                <span class="text-sm font-medium">${location.status === 'active' ? 'Active' : 'Down'}</span>
              </div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Region:</div>
                <div>${location.region}</div>
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Security:</div>
                <div>${location.securityLevel}</div>
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Storage:</div>
                <div>${location.storage}</div>
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Connections:</div>
                <div>${location.connections}</div>
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Uptime:</div>
                <div>${location.uptime}</div>
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Encryption:</div>
                <div>${location.encryptionType}</div>
                <div class="${isDark ? 'text-gray-400' : 'text-gray-500'}">Last updated:</div>
                <div>${location.lastUpdated}</div>
              </div>
            </div>
          `;
          
          popupsRef.current[location.id].innerHTML = htmlString;
        };

        // Add event listeners
        markerEl.addEventListener('mouseenter', () => {
          renderPopup();
          popup.addTo(map.current!);
        });
        
        markerEl.addEventListener('mouseleave', () => {
          popup.remove();
        });

        markersRef.current.push(marker);
      });

      console.log(`Added ${markersRef.current.length} markers to the map`);
    } catch (error) {
      console.error('Error adding markers to map:', error);
      toast({
        title: "Marker Error",
        description: `Failed to add location markers: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }

    return () => {
      cleanupMarkers();
    };
  }, [mapLoaded, locations, isDark]);

  return {
    cleanupMarkers
  };
};
